import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { caseAPI, clientAPI, documentAPI, activityAPI, caseServiceAPI, visaProgramAPI, checklistAPI, signAPI, api, emailAPI, libraryAPI, fileUtils, familyAPI, completeFileAPI } from '../utils/api';
import EscalationModal from '../components/EscalationModal';
import {
  ArrowLeft, User, FileText, Clock, CheckCircle, XCircle,
  AlertTriangle, Upload, MessageSquare, Calendar, Phone, Mail,
  Globe, CreditCard, Send, Eye, Download, Flag, Bell,
  ChevronRight, ChevronDown, Loader2, CheckSquare, Square, StickyNote, FileSignature,
  Library, Share2, Edit3, RefreshCw, Plus, Trash2, X, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import CaseNotes from '../components/CaseNotes';
import CaseProgressBar from '../components/CaseProgressBar';
import DocumentViewer from '../components/DocumentViewer';
import { useAuth } from '../context/AuthContext';

export default function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [caseData, setCaseData] = useState(null);
  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [processStages, setProcessStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviewingDoc, setReviewingDoc] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [officerNotes, setOfficerNotes] = useState('');
  const [taskChecklist, setTaskChecklist] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showSampleUploadModal, setShowSampleUploadModal] = useState(false);
  const [sampleDocName, setSampleDocName] = useState('');
  const [sampleDocDescription, setSampleDocDescription] = useState('');
  const [sampleDocFile, setSampleDocFile] = useState(null);
  const [uploadingSample, setUploadingSample] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showSampleDocMenu, setShowSampleDocMenu] = useState(false);
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [caseEmails, setCaseEmails] = useState([]);
  const [showEmailViewModal, setShowEmailViewModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailForm, setEmailForm] = useState({ to_email: '', subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [showChangeVisaModal, setShowChangeVisaModal] = useState(false);
  const [visaPrograms, setVisaPrograms] = useState([]);
  const [selectedVisaProgram, setSelectedVisaProgram] = useState('');
  const [changingVisaProgram, setChangingVisaProgram] = useState(false);

  // Custom document requests
  const [customRequests, setCustomRequests]         = useState([]);
  const [showReqModal, setShowReqModal]             = useState(false);
  const [reqForm, setReqForm]                       = useState({ name: '', description: '', applicant_type: 'primary', applicant_id: '', applicant_name: '', required_by: '' });
  const [savingReq, setSavingReq]                   = useState(false);
  // Escalation
  const [showEscalation, setShowEscalation]         = useState(false);
  // Complete File
  const [completeFile, setCompleteFile]             = useState(null);
  const [cfLoading, setCfLoading]                   = useState(false);
  const [showGovFormModal, setShowGovFormModal]     = useState(false);
  const [govFormName, setGovFormName]               = useState('');
  const [govFormDesc, setGovFormDesc]               = useState('');
  const [govFormFile, setGovFormFile]               = useState(null);
  const [uploadingGovForm, setUploadingGovForm]     = useState(false);

  useEffect(() => {
    fetchData();
    fetchVisaPrograms();
  }, [caseId]);

  const fetchVisaPrograms = async () => {
    try {
      const res = await visaProgramAPI.getPrograms();
      setVisaPrograms(res.data || []);
    } catch (err) {
      console.log('Could not fetch visa programs');
    }
  };

  const handleChangeVisaProgram = async () => {
    if (!selectedVisaProgram) {
      toast.error('Please select a visa program');
      return;
    }
    
    setChangingVisaProgram(true);
    try {
      const res = await caseAPI.changeVisaProgram(caseId, selectedVisaProgram);
      toast.success(`Visa program changed successfully! New validity: ${res.data.processing_days} days`);
      setShowChangeVisaModal(false);
      setSelectedVisaProgram('');
      // Refresh case data
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change visa program');
    } finally {
      setChangingVisaProgram(false);
    }
  };

  const fetchData = async () => {
    try {
      const caseRes = await caseAPI.getCase(caseId);
      setCaseData(caseRes.data);

      // Fetch docs and timeline
      const [docsRes, timelineRes] = await Promise.all([
        documentAPI.getCaseDocuments(caseId),
        activityAPI.getCaseTimeline(caseId)
      ]);
      
      setDocuments(docsRes.data);
      setTimeline(timelineRes.data);

      // Fetch client (may not exist)
      try {
        const clientRes = await clientAPI.getClient(caseRes.data.client_id);
        setClient(clientRes.data);
        
        // Fetch payment info (role-based)
        try {
          const paymentRes = await api.get(`/clients/${clientRes.data.id}/payment-info`);
          setPaymentInfo(paymentRes.data);
        } catch (err) {
          console.log('Could not fetch payment info');
        }
      } catch (err) {
        console.log('Client not found - using case data');
        setClient({ full_name: 'Unknown Client', email: 'N/A', phone: 'N/A' });
      }

      // Fetch process stages
      try {
        const stagesRes = await caseServiceAPI.getProcessStages(caseId);
        setProcessStages(stagesRes.data.stages || []);
      } catch (err) {
        console.log('Could not fetch process stages');
      }

      // Fetch time remaining
      try {
        const timeRes = await caseServiceAPI.getTimeRemaining(caseId);
        setTimeRemaining(timeRes.data);
      } catch (err) {
        console.log('Could not fetch time remaining');
      }

      // Fetch checklist
      try {
        const checklistRes = await checklistAPI.getChecklistByProgram(caseRes.data.visa_program_id);
        setChecklist(checklistRes.data.items || []);
      } catch (err) {
        setChecklist([]);
      }

      // Fetch custom document requests
      try {
        const reqRes = await familyAPI.getCustomRequests(caseId);
        setCustomRequests(reqRes.data || []);
      } catch (err) {
        setCustomRequests([]);
      }

      // Fetch complete file
      try {
        const cfRes = await completeFileAPI.get(caseId);
        setCompleteFile(cfRes.data);
      } catch (err) {
        setCompleteFile(null);
      }

      // Generate task checklist based on current stage
      generateTaskChecklist(caseRes.data);

    } catch (error) {
      toast.error('Failed to load case details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const generateTaskChecklist = (caseData) => {
    const stage = caseData.current_process_stage;
    let tasks = [];

    // Common tasks
    tasks.push({ id: 'review_client_info', label: 'Review client information', done: true });
    
    if (stage === 'Document Checklist Upload' || stage === 'Documents Verification') {
      tasks.push({ id: 'check_docs', label: 'Check all uploaded documents', done: false });
      tasks.push({ id: 'verify_passport', label: 'Verify passport validity (6+ months)', done: false });
      tasks.push({ id: 'verify_bank', label: 'Verify bank statements match requirements', done: false });
      tasks.push({ id: 'check_photos', label: 'Check photo specifications', done: false });
      tasks.push({ id: 'note_missing', label: 'Note any missing documents', done: false });
    }

    if (stage === 'Final Application Review') {
      tasks.push({ id: 'prepare_app', label: 'Prepare final application form', done: false });
      tasks.push({ id: 'double_check', label: 'Double-check all entries', done: false });
      tasks.push({ id: 'upload_final', label: 'Upload final application for client review', done: false });
    }

    if (stage === 'Visa Fee Payment') {
      tasks.push({ id: 'confirm_fee', label: 'Confirm visa fee amount', done: false });
      tasks.push({ id: 'send_payment', label: 'Send payment instructions to client', done: false });
      tasks.push({ id: 'verify_payment', label: 'Verify payment received', done: false });
    }

    if (stage === 'Visa Application Filing') {
      tasks.push({ id: 'submit_app', label: 'Submit application to embassy/consulate', done: false });
      tasks.push({ id: 'get_receipt', label: 'Get submission receipt/reference number', done: false });
      tasks.push({ id: 'notify_client', label: 'Notify client of submission', done: false });
    }

    setTaskChecklist(tasks);
  };

  const toggleTask = (taskId) => {
    setTaskChecklist(prev => 
      prev.map(t => t.id === taskId ? { ...t, done: !t.done } : t)
    );
  };

  const handleSendReminder = async () => {
    try {
      await api.post(`/cases/${caseId}/send-reminder`);
      toast.success('Reminder sent successfully!');
      setShowReminderModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleUploadSampleDoc = async () => {
    if (!sampleDocFile || !sampleDocName) {
      toast.error('Please provide a document name and file');
      return;
    }

    setUploadingSample(true);
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      formData.append('name', sampleDocName);
      formData.append('description', sampleDocDescription);
      formData.append('file', sampleDocFile);

      await api.post('/sample-documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Sample document uploaded successfully!');
      setShowSampleUploadModal(false);
      setSampleDocName('');
      setSampleDocDescription('');
      setSampleDocFile(null);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload sample document');
    } finally {
      setUploadingSample(false);
    }
  };

  // Fetch emails for this case
  const fetchCaseEmails = async () => {
    try {
      const res = await emailAPI.getCaseEmails(caseId);
      setCaseEmails(res.data);
    } catch (error) {
      console.error('Failed to load case emails');
    }
  };

  // Fetch library documents
  const fetchLibraryDocs = async () => {
    try {
      const res = await libraryAPI.getDocuments();
      setLibraryDocs(res.data);
    } catch (error) {
      console.error('Failed to load library documents');
    }
  };

  // Open email compose modal
  const openEmailModal = () => {
    setEmailForm({
      to_email: client?.email || '',
      subject: `Regarding Your Visa Application - Case ${caseData?.ucin || caseId.substring(0, 8)}`,
      body: `Dear ${client?.full_name},\n\n\n\nBest regards,\nImmiways Migration Team`
    });
    setShowEmailModal(true);
  };

  // Send email from case
  const handleSendEmail = async () => {
    if (!emailForm.to_email || !emailForm.subject || !emailForm.body) {
      toast.error('Please fill in all fields');
      return;
    }

    setSendingEmail(true);
    try {
      await emailAPI.sendEmail({
        ...emailForm,
        case_id: caseId,
        client_id: client?.id
      });
      toast.success('Email sent successfully');
      setShowEmailModal(false);
      setEmailForm({ to_email: '', subject: '', body: '' });
      fetchCaseEmails();
      fetchData(); // Refresh timeline
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // View email detail
  const handleViewEmail = async (email) => {
    setSelectedEmail(email);
    setShowEmailViewModal(true);
  };

  // Share document from library
  const handleShareFromLibrary = async (docId) => {
    try {
      await libraryAPI.shareDocument(docId, caseId);
      toast.success('Document shared successfully');
      setShowLibraryModal(false);
      fetchData(); // Refresh to show new sample doc
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share document');
    }
  };

  const handleDocumentReview = async (docId, status) => {
    if (!reviewNotes && status === 'rejected') {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setSubmitting(true);
    try {
      await documentAPI.updateDocument(docId, {
        status,
        reviewer_notes: reviewNotes
      });
      toast.success(`Document ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      setReviewingDoc(null);
      setReviewNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update document status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await caseAPI.updateCase(caseId, { stage: newStage });
      toast.success('Case stage updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const getDocumentStatus = (itemName) => {
    const doc = documents.find(d => d.checklist_item_name === itemName);
    if (!doc) return { status: 'missing', doc: null };
    return { status: doc.status, doc };
  };

  const getPendingDocuments = () => documents.filter(d => d.status === 'uploaded');
  const getApprovedDocuments = () => documents.filter(d => d.status === 'approved');
  const getRejectedDocuments = () => documents.filter(d => d.status === 'rejected');

  // ── Custom document request handlers ──────────────────────────────────────
  const handleAddRequest = async () => {
    if (!reqForm.name.trim()) { toast.error('Document name is required'); return; }
    setSavingReq(true);
    try {
      const payload = {
        name: reqForm.name,
        description: reqForm.description || null,
        applicant_type: reqForm.applicant_type,
        applicant_id: reqForm.applicant_id || null,
        applicant_name: reqForm.applicant_name || null,
        required_by: reqForm.required_by || null,
      };
      await familyAPI.addCustomRequest(caseId, payload);
      toast.success(`Document request sent to client`);
      setShowReqModal(false);
      setReqForm({ name: '', description: '', applicant_type: 'primary', applicant_id: '', applicant_name: '', required_by: '' });
      const res = await familyAPI.getCustomRequests(caseId);
      setCustomRequests(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create request');
    } finally {
      setSavingReq(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Remove this document request?')) return;
    try {
      await familyAPI.deleteCustomRequest(caseId, requestId);
      toast.success('Request removed');
      setCustomRequests(p => p.filter(r => r.id !== requestId));
    } catch {
      toast.error('Failed to remove request');
    }
  };

  // ── Complete File handlers ─────────────────────────────────────────────────
  const handleGenerateCf = async () => {
    setCfLoading(true);
    try {
      const res = await completeFileAPI.generate(caseId);
      setCompleteFile(res.data);
      toast.success('Complete File generated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    } finally { setCfLoading(false); }
  };

  const handleSendCfToClient = async () => {
    if (!window.confirm('Send the Complete File to the client for review? They will be notified immediately.')) return;
    setCfLoading(true);
    try {
      const res = await completeFileAPI.sendToClient(caseId);
      setCompleteFile(res.data);
      toast.success('Complete File sent to client for review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setCfLoading(false); }
  };

  const handleUploadGovForm = async () => {
    if (!govFormName.trim() || !govFormFile) { toast.error('Form name and file are required'); return; }
    setUploadingGovForm(true);
    try {
      const fd = new FormData();
      fd.append('name', govFormName.trim());
      fd.append('description', govFormDesc.trim());
      fd.append('file', govFormFile);
      const res = await completeFileAPI.uploadGovForm(caseId, fd);
      setCompleteFile(res.data);
      setShowGovFormModal(false);
      setGovFormName(''); setGovFormDesc(''); setGovFormFile(null);
      toast.success('Government form uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploadingGovForm(false); }
  };

  const handleDeleteGovForm = async (formId) => {
    if (!window.confirm('Remove this government form?')) return;
    try {
      const res = await completeFileAPI.deleteGovForm(caseId, formId);
      setCompleteFile(res.data);
      toast.success('Form removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleDownloadLegalRecord = async () => {
    try {
      const res = await completeFileAPI.getLegalRecord(caseId);
      const rec = res.data;
      const ap = rec.approval;

      const fmtDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'full', timeStyle: 'long' });
      };

      const docLine = (n, label, type) =>
        `<tr><td style="padding:4px 8px;color:#64748b;font-size:12px;">${n}</td><td style="padding:4px 8px;font-size:12px;">${label}</td><td style="padding:4px 8px;font-size:11px;color:#94a3b8;">${type}</td></tr>`;

      const allDocs = [
        ...(rec.documents.client_uploaded || []).map(d => docLine('●', `${d.checklist_item_name}${d.document_side_label ? ' — ' + d.document_side_label : ''}`, 'Client Uploaded')),
        ...(rec.documents.officer_prepared || []).map(d => docLine('●', d.checklist_item_name, 'Officer Prepared')),
        ...(rec.documents.government_forms || []).map(f => docLine('●', f.name, 'Government Form')),
      ].join('');

      const acks = [ap.acknowledgement_1, ap.acknowledgement_2, ap.acknowledgement_3]
        .filter(Boolean)
        .map(a => `<li style="margin-bottom:6px;font-size:13px;color:#334155;">✓ &nbsp;${a}</li>`)
        .join('');

      const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Legal Approval Certificate — ${rec.case.ucin}</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 40px; color: #1e293b; background: #fff; }
    .header { border-bottom: 3px solid #16a34a; padding-bottom: 20px; margin-bottom: 28px; }
    .company { font-size: 22px; font-weight: 800; color: #15803d; letter-spacing: -0.5px; }
    .doc-title { font-size: 15px; font-weight: 600; color: #374151; margin-top: 4px; }
    .section-title { font-size: 11px; font-weight: 700; color: #16a34a; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #dcfce7; padding-bottom: 4px; margin: 22px 0 10px; }
    .field { display: grid; grid-template-columns: 200px 1fr; gap: 4px; margin-bottom: 6px; font-size: 13px; }
    .label { color: #64748b; }
    .value { font-weight: 600; color: #1e293b; }
    .mono { font-family: 'Courier New', monospace; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f8fafc; text-align: left; padding: 6px 8px; font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; }
    ul { margin: 0; padding-left: 0; list-style: none; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; display: flex; justify-content: space-between; }
    .badge { display: inline-block; background: #dcfce7; color: #15803d; padding: 2px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; margin-left: 8px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="company">ImmiWays Migration Advisory</div>
    <div class="doc-title">CLIENT APPLICATION APPROVAL CERTIFICATE <span class="badge">LEGALLY BINDING</span></div>
  </div>

  <div class="section-title">Case Reference</div>
  <div class="field"><span class="label">Case Number (UCIN)</span><span class="value">${rec.case.ucin || '—'}</span></div>
  <div class="field"><span class="label">Visa Program</span><span class="value">${rec.case.visa_program || '—'}</span></div>
  <div class="field"><span class="label">Case Officer</span><span class="value">${rec.case.case_officer || '—'}</span></div>

  <div class="section-title">Client Identity</div>
  <div class="field"><span class="label">Full Name</span><span class="value">${ap.client_name || '—'}</span></div>
  <div class="field"><span class="label">Email Address</span><span class="value">${ap.client_email || '—'}</span></div>

  <div class="section-title">Approval — Digital Evidence</div>
  <div class="field"><span class="label">Date &amp; Time (IST)</span><span class="value">${fmtDate(ap.approved_at_utc)}</span></div>
  <div class="field"><span class="label">Date &amp; Time (UTC)</span><span class="value">${new Date(ap.approved_at_utc).toUTCString()}</span></div>
  <div class="field"><span class="label">Client IP Address</span><span class="value mono">${ap.ip_address || '—'}</span></div>
  <div class="field"><span class="label">Browser / Device</span><span class="value" style="font-size:12px;word-break:break-all;">${ap.browser_device || '—'}</span></div>
  <div class="field"><span class="label">Record ID</span><span class="value mono" style="font-size:11px;">${rec.record_id || '—'}</span></div>
  <div class="field"><span class="label">Total Documents</span><span class="value">${ap.total_documents} documents acknowledged</span></div>

  <div class="section-title">Client Acknowledgements (All Individually Confirmed)</div>
  <ul>${acks}</ul>

  <div class="section-title">Documents Included in Approved File</div>
  <table>
    <thead><tr><th>#</th><th>Document Name</th><th>Type</th></tr></thead>
    <tbody>${allDocs}</tbody>
  </table>

  <div class="section-title">System Record</div>
  <div class="field"><span class="label">File Generated By</span><span class="value">${rec.complete_file_generated_by || '—'}</span></div>
  <div class="field"><span class="label">File Generated At</span><span class="value">${fmtDate(rec.complete_file_generated_at)}</span></div>
  <div class="field"><span class="label">Sent to Client At</span><span class="value">${fmtDate(rec.complete_file_sent_to_client_at)}</span></div>
  <div class="field"><span class="label">Certificate Generated</span><span class="value">${fmtDate(rec.generated_at)}</span></div>

  <div style="margin-top:30px;padding:16px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;font-size:12px;color:#15803d;">
    <strong>Legal Declaration:</strong> This certificate constitutes a legally binding digital record confirming that the above-named client has individually reviewed, verified, and approved all documents listed herein prior to visa application filing. This approval was recorded electronically by ImmiWays CRM with the client's IP address, browser identity, and exact timestamp as evidence of informed consent and intent.
  </div>

  <div class="footer">
    <span>Generated by ImmiWays CRM &nbsp;|&nbsp; ${rec.company.name}</span>
    <span>Record ID: ${rec.record_id}</span>
    <span>DO NOT ALTER — Legal Document</span>
  </div>
</body>
</html>`;

      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Legal_Approval_${rec.case.ucin}_${new Date().toISOString().split('T')[0]}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Legal record downloaded. Open in browser and Print → Save as PDF.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download legal record');
    }
  };

  const getUrgencyLevel = () => {
    if (!timeRemaining) return 'normal';
    if (timeRemaining.is_expired) return 'critical';
    if (timeRemaining.days_remaining < 7) return 'high';
    if (timeRemaining.days_remaining < 30) return 'medium';
    return 'normal';
  };

  const urgencyColors = {
    critical: 'bg-red-100 text-red-700 border-red-300',
    high: 'bg-orange-100 text-orange-700 border-orange-300',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    normal: 'bg-green-100 text-green-700 border-green-300'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!caseData || !client) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content">
          <p className="text-center text-slate-500 py-12">Case not found</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'documents', label: 'Documents', icon: FileText, badge: getPendingDocuments().length },
    { id: 'timeline', label: 'Timeline', icon: Clock },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare, badge: taskChecklist.filter(t => !t.done).length },
    { id: 'notes', label: 'Notes', icon: StickyNote },
  ];

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="case-detail-page">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft size={20} />
            Back to Cases
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-slate-900">
                  Case {caseData?.ucin || `#${caseId.slice(0, 8)}`}
                </h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${urgencyColors[getUrgencyLevel()]}`}>
                  {getUrgencyLevel() === 'critical' ? '⚠️ Expired' : 
                   getUrgencyLevel() === 'high' ? '🔴 Urgent' :
                   getUrgencyLevel() === 'medium' ? '🟡 Attention' : '🟢 On Track'}
                </span>
              </div>
              <p className="text-slate-600">{client.full_name} • {client.visa_type} • {client.country_applying_to}</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowEscalation(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold text-sm hover:bg-red-700 transition-all"
                title="Raise Legal Escalation"
              >
                <Shield size={16} />
                Escalate
              </button>
              <select
                value={caseData.stage}
                onChange={(e) => handleStageChange(e.target.value)}
                className="px-4 py-2 bg-white border border-slate-200 rounded-lg font-medium"
              >
                <option value="New Client">New Client</option>
                <option value="Agreement Sent">Agreement Sent</option>
                <option value="Agreement Signed">Agreement Signed</option>
                <option value="Documents Pending">Documents Pending</option>
                <option value="Documents Submitted">Documents Submitted</option>
                <option value="Under Review">Under Review</option>
                <option value="Application Filed">Application Filed</option>
                <option value="Decision Pending">Decision Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Days Remaining</p>
                <p className="text-xl font-bold text-slate-900">
                  {timeRemaining?.is_expired ? 'Expired' : timeRemaining?.days_remaining || '--'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <FileText className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Pending Review</p>
                <p className="text-xl font-bold text-slate-900">{getPendingDocuments().length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Approved</p>
                <p className="text-xl font-bold text-slate-900">{getApprovedDocuments().length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Rejected</p>
                <p className="text-xl font-bold text-slate-900">{getRejectedDocuments().length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Case Progress Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="case-progress-section">
          <CaseProgressBar 
            progress={caseData?.progress_percentage || 0} 
            showMilestones={true} 
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-teal-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon size={18} />
                {tab.label}
                {tab.badge > 0 && (
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    activeTab === tab.id ? 'bg-white text-teal-600' : 'bg-red-500 text-white'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-3 gap-6">
            {/* Client Information */}
            <div className="col-span-2 space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <User size={20} className="text-teal-600" />
                  Client Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Full Name</p>
                    <p className="font-medium text-slate-900">{client.full_name}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium text-slate-900 flex items-center gap-2 truncate" title={client.email}>
                      <Mail size={14} className="flex-shrink-0" /> 
                      <span className="truncate">{client.email}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium text-slate-900 flex items-center gap-2">
                      <Phone size={14} /> {client.phone}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Passport Number</p>
                    <p className="font-medium text-slate-900">{client.passport_number || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Nationality</p>
                    <p className="font-medium text-slate-900 flex items-center gap-2">
                      <Globe size={14} /> {client.nationality}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Applying To</p>
                    <p className="font-medium text-slate-900">{client.country_applying_to}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Visa Type</p>
                    <p className="font-medium text-slate-900">{client.visa_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Payment Status</p>
                    {user?.role === 'case_officer' ? (
                      // Case officer sees limited payment info
                      <div>
                        <p className="font-medium text-slate-900 flex items-center gap-2">
                          <CreditCard size={14} />
                          {paymentInfo?.payment_status_display || 'N/A'}
                        </p>
                        {paymentInfo?.balance_display && (
                          <p className={`text-xs mt-1 ${paymentInfo.balance_display === 'DUE' ? 'text-amber-600 font-medium' : 'text-green-600'}`}>
                            Balance: {paymentInfo.balance_display}
                            {paymentInfo.balance_due_date && (
                              <span className="ml-2">(Due: {new Date(paymentInfo.balance_due_date).toLocaleDateString()})</span>
                            )}
                          </p>
                        )}
                      </div>
                    ) : (
                      // Admin sees full payment details
                      <p className="font-medium text-slate-900 flex items-center gap-2">
                        <CreditCard size={14} /> ₹{client.payment_amount?.toLocaleString('en-IN')} ({client.payment_status === 'full_paid' ? 'Fully Paid' : client.payment_status === 'partial_paid' ? 'Partial' : client.payment_status})
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Sales Consultant</p>
                    <p className="font-medium text-slate-900">{client.sales_consultant || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Customer Type</p>
                    <p className="font-medium text-slate-900">{client.customer_type || 'Inhouse'}</p>
                  </div>
                </div>
              </div>

              {/* Process Stages */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Process Stages</h3>
                <div className="space-y-3">
                  {processStages.map((stage, index) => (
                    <div 
                      key={stage.stage}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        stage.is_current ? 'bg-teal-50 border border-teal-200' :
                        stage.is_completed ? 'bg-green-50' : 'bg-slate-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        stage.is_completed ? 'bg-green-500 text-white' :
                        stage.is_current ? 'bg-teal-500 text-white' : 'bg-slate-300 text-slate-600'
                      }`}>
                        {stage.is_completed ? <CheckCircle size={16} /> : index + 1}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${
                          stage.is_current ? 'text-teal-700' :
                          stage.is_completed ? 'text-green-700' : 'text-slate-500'
                        }`}>
                          {stage.stage}
                        </p>
                      </div>
                      {stage.is_current && (
                        <span className="px-2 py-1 bg-teal-500 text-white text-xs rounded-full">Current</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  {/* Change Visa Program - Admin/Super Admin only */}
                  {(user?.role === 'super_admin' || user?.role === 'process_manager') && (
                    <button 
                      onClick={() => {
                        setSelectedVisaProgram(caseData?.visa_program_id || '');
                        setShowChangeVisaModal(true);
                      }}
                      className="w-full px-4 py-3 bg-amber-50 text-amber-700 rounded-lg font-medium hover:bg-amber-100 transition-all flex items-center gap-2"
                      data-testid="change-visa-program-btn"
                    >
                      <Edit3 size={18} />
                      Change Visa Program
                    </button>
                  )}
                  <button 
                    onClick={() => setActiveTab('documents')}
                    className="w-full px-4 py-3 bg-teal-50 text-teal-700 rounded-lg font-medium hover:bg-teal-100 transition-all flex items-center gap-2"
                  >
                    <FileText size={18} />
                    Review Documents
                    {getPendingDocuments().length > 0 && (
                      <span className="ml-auto px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full">
                        {getPendingDocuments().length}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => setShowReminderModal(true)}
                    className="w-full px-4 py-3 bg-blue-50 text-blue-700 rounded-lg font-medium hover:bg-blue-100 transition-all flex items-center gap-2"
                  >
                    <Send size={18} />
                    Send Reminder
                  </button>
                  <div className="relative">
                    <button 
                      onClick={() => setShowSampleDocMenu(!showSampleDocMenu)}
                      className="w-full px-4 py-3 bg-purple-50 text-purple-700 rounded-lg font-medium hover:bg-purple-100 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Upload size={18} />
                        Upload Sample Doc
                      </div>
                      <ChevronDown size={16} className={`transition-transform ${showSampleDocMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showSampleDocMenu && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden z-10">
                        <button
                          onClick={() => { setShowSampleUploadModal(true); setShowSampleDocMenu(false); }}
                          className="w-full px-4 py-3 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <Upload size={16} />
                          Upload from Computer
                        </button>
                        <button
                          onClick={() => { fetchLibraryDocs(); setShowLibraryModal(true); setShowSampleDocMenu(false); }}
                          className="w-full px-4 py-3 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2 border-t"
                        >
                          <Library size={16} />
                          Select from Library
                        </button>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={openEmailModal}
                    className="w-full px-4 py-3 bg-green-50 text-green-700 rounded-lg font-medium hover:bg-green-100 transition-all flex items-center gap-2"
                  >
                    <Mail size={18} />
                    Send Email
                  </button>
                  <button 
                    onClick={() => setActiveTab('notes')}
                    className="w-full px-4 py-3 bg-amber-50 text-amber-700 rounded-lg font-medium hover:bg-amber-100 transition-all flex items-center gap-2"
                  >
                    <MessageSquare size={18} />
                    Add Note
                  </button>
                </div>
              </div>

              {/* Service Validity */}
              <div className={`rounded-xl border p-6 ${
                timeRemaining?.is_expired ? 'bg-red-50 border-red-200' :
                timeRemaining?.days_remaining < 7 ? 'bg-amber-50 border-amber-200' :
                'bg-white border-slate-200'
              }`}>
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Calendar size={20} />
                  Service Validity
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-500">Start Date</p>
                    <p className="font-medium">{new Date(caseData.service_start_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">End Date</p>
                    <p className="font-medium">{new Date(caseData.service_end_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Validity Period</p>
                    <p className="font-medium">{caseData.service_validity_months} months</p>
                  </div>
                  {timeRemaining?.is_expired && (
                    <div className="p-3 bg-red-100 rounded-lg">
                      <p className="text-sm text-red-700 font-medium">⚠️ Service period has expired. Consider extending.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {timeline.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5"></div>
                      <div>
                        <p className="text-slate-700">{activity.description}</p>
                        <p className="text-slate-400 text-xs">{new Date(activity.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Document Checklist</h3>
                <div className="space-y-3">
                  {checklist.map((item) => {
                    const { status, doc } = getDocumentStatus(item.name);
                    return (
                      <div 
                        key={item.name}
                        className={`p-4 rounded-lg border ${
                          status === 'approved' ? 'bg-green-50 border-green-200' :
                          status === 'rejected' ? 'bg-red-50 border-red-200' :
                          status === 'uploaded' ? 'bg-amber-50 border-amber-200' :
                          'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {status === 'approved' ? (
                              <CheckCircle size={22} className="text-green-500 mt-0.5" />
                            ) : status === 'rejected' ? (
                              <XCircle size={22} className="text-red-500 mt-0.5" />
                            ) : status === 'uploaded' ? (
                              <Clock size={22} className="text-amber-500 mt-0.5" />
                            ) : (
                              <AlertTriangle size={22} className="text-slate-400 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium text-slate-900">
                                {item.name}
                                {item.required && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              {item.description && (
                                <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                              )}
                              {doc && (
                                <div className="mt-2 flex items-center gap-2">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    status === 'approved' ? 'bg-green-100 text-green-700' :
                                    status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    'bg-amber-100 text-amber-700'
                                  }`}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {doc.file_name}
                                  </span>
                                </div>
                              )}
                              {doc?.reviewer_notes && (
                                <p className="text-sm text-slate-600 mt-2 italic">
                                  Note: {doc.reviewer_notes}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {doc && (
                              <>
                                <button
                                  onClick={() => setViewingDocument(doc)}
                                  className="p-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                                  title="View Document"
                                  data-testid={`view-doc-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  onClick={() => fileUtils.downloadFile(doc.file_url, doc.file_name)}
                                  className="p-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                                  title="Download Document"
                                  data-testid={`download-doc-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <Download size={16} />
                                </button>
                              </>
                            )}
                            {status === 'uploaded' && (
                              <button
                                onClick={() => setReviewingDoc(doc)}
                                className="px-3 py-1.5 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 flex items-center gap-1"
                                data-testid={`review-doc-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <CheckSquare size={14} />
                                Review
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Custom Document Requests ── */}
            <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Additional Document Requests</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Request specific documents from the client — they will be notified immediately</p>
                </div>
                <button
                  onClick={() => setShowReqModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-all"
                >
                  <Plus size={15} /> Request Document
                </button>
              </div>

              {customRequests.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                  <FileText size={32} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">No additional document requests yet</p>
                  <p className="text-xs text-slate-300 mt-0.5">Use this to request affidavits, declarations, or case-specific documents</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {customRequests.map(req => {
                    const uploaded = documents.find(d => d.custom_request_id === req.id);
                    const isOverdue = req.required_by && req.required_by < new Date().toISOString().split('T')[0] && !uploaded;
                    return (
                      <div key={req.id} className={`flex items-start justify-between p-4 rounded-xl border ${uploaded ? 'bg-green-50 border-green-200' : isOverdue ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {uploaded
                            ? <CheckCircle size={18} className="text-green-500 mt-0.5 shrink-0" />
                            : isOverdue
                              ? <AlertTriangle size={18} className="text-red-500 mt-0.5 shrink-0" />
                              : <Clock size={18} className="text-amber-500 mt-0.5 shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 text-sm">{req.name}</p>
                            {req.applicant_name && (
                              <p className="text-xs text-slate-500">For: {req.applicant_name} ({req.applicant_type})</p>
                            )}
                            {req.description && <p className="text-xs text-slate-500 mt-0.5">{req.description}</p>}
                            <div className="flex items-center gap-3 mt-1">
                              {req.required_by && (
                                <span className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-slate-500'}`}>
                                  Due: {new Date(req.required_by).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  {isOverdue && ' (overdue)'}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${uploaded ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {uploaded ? 'Uploaded' : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button onClick={() => handleDeleteRequest(req.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all ml-2 shrink-0">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Complete File Management ── */}
            <div className="col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Header */}
              <div className={`px-6 py-4 flex items-center justify-between ${completeFile?.status === 'approved' ? 'bg-green-50 border-b border-green-200' : completeFile?.status === 'pending_client' ? 'bg-indigo-50 border-b border-indigo-200' : completeFile?.status === 'changes_requested' ? 'bg-amber-50 border-b border-amber-200' : 'bg-slate-50 border-b border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <Shield size={20} className={completeFile?.status === 'approved' ? 'text-green-600' : completeFile?.status === 'pending_client' ? 'text-indigo-600' : 'text-slate-500'} />
                  <div>
                    <p className="font-bold text-slate-900">Complete File</p>
                    <p className="text-xs text-slate-500">
                      {!completeFile ? 'Not generated yet'
                        : completeFile.status === 'draft' ? `Draft — ${(completeFile.client_documents?.length || 0) + (completeFile.officer_documents?.length || 0)} documents, ${completeFile.government_forms?.length || 0} government forms`
                        : completeFile.status === 'pending_client' ? 'Sent to client — awaiting approval'
                        : completeFile.status === 'changes_requested' ? 'Client requested changes'
                        : `Approved by client on ${new Date(completeFile.approval?.approved_at).toLocaleDateString('en-IN')}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {completeFile?.status === 'changes_requested' && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold">Needs Revision</span>
                  )}
                  {(!completeFile || completeFile.status === 'draft' || completeFile.status === 'changes_requested') && (
                    <button onClick={handleGenerateCf} disabled={cfLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white rounded-lg text-xs font-medium hover:bg-slate-800 disabled:opacity-50">
                      {cfLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                      {completeFile ? 'Regenerate' : 'Generate'}
                    </button>
                  )}
                  {completeFile && completeFile.status !== 'approved' && (
                    <button onClick={() => setShowGovFormModal(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600">
                      <Plus size={13} /> Add Gov. Form
                    </button>
                  )}
                  {completeFile?.status === 'draft' && (
                    <button onClick={handleSendCfToClient} disabled={cfLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 disabled:opacity-50">
                      <Send size={13} /> Send to Client
                    </button>
                  )}
                </div>
              </div>

              {/* Client feedback banner */}
              {completeFile?.status === 'changes_requested' && completeFile.client_feedback && (
                <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-300 rounded-xl">
                  <p className="text-xs font-bold text-amber-800 mb-1">Client Feedback:</p>
                  <p className="text-sm text-amber-900">"{completeFile.client_feedback}"</p>
                  <p className="text-xs text-amber-600 mt-1">{new Date(completeFile.feedback_at).toLocaleString('en-IN')}</p>
                </div>
              )}

              {/* Full Legal Approval Record */}
              {completeFile?.status === 'approved' && completeFile.approval && (
                <div className="mx-6 mt-4 border border-green-300 rounded-xl overflow-hidden">
                  {/* Record header */}
                  <div className="bg-green-600 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white">
                      <Shield size={16} />
                      <span className="font-bold text-sm">Legal Approval Certificate</span>
                      <span className="text-green-200 text-xs">— Admissible Evidence</span>
                    </div>
                    <button
                      onClick={() => handleDownloadLegalRecord()}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-green-700 rounded-lg text-xs font-bold hover:bg-green-50 transition-all"
                    >
                      <Download size={13} /> Download Record
                    </button>
                  </div>

                  <div className="bg-green-50 p-5 space-y-4">
                    {/* Client identity */}
                    <div>
                      <p className="text-xs font-bold text-green-800 uppercase tracking-wider mb-2">Client Identity</p>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-xs">
                        <div><span className="text-slate-500">Full Name</span><p className="font-semibold text-slate-800">{completeFile.approval.client_name}</p></div>
                        <div><span className="text-slate-500">Email Address</span><p className="font-semibold text-slate-800">{completeFile.approval.client_email || '—'}</p></div>
                      </div>
                    </div>

                    <div className="border-t border-green-200" />

                    {/* Digital proof */}
                    <div>
                      <p className="text-xs font-bold text-green-800 uppercase tracking-wider mb-2">Digital Evidence</p>
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-xs">
                        <div>
                          <span className="text-slate-500">Date &amp; Time (IST)</span>
                          <p className="font-semibold text-slate-800">{new Date(completeFile.approval.approved_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'long', timeStyle: 'medium' })}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Date &amp; Time (UTC)</span>
                          <p className="font-semibold text-slate-800">{new Date(completeFile.approval.approved_at).toUTCString()}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">IP Address</span>
                          <p className="font-semibold text-slate-800 font-mono">{completeFile.approval.client_ip_address}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Browser / Device</span>
                          <p className="font-semibold text-slate-800 truncate" title={completeFile.approval.client_user_agent}>
                            {completeFile.approval.client_user_agent
                              ? completeFile.approval.client_user_agent.slice(0, 60) + (completeFile.approval.client_user_agent.length > 60 ? '…' : '')
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-500">Total Documents Acknowledged</span>
                          <p className="font-bold text-slate-800">{completeFile.approval.total_documents}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Record ID</span>
                          <p className="font-semibold text-slate-600 font-mono text-xs">{completeFile.id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-green-200" />

                    {/* Acknowledgements */}
                    <div>
                      <p className="text-xs font-bold text-green-800 uppercase tracking-wider mb-2">Client Acknowledgements (All Confirmed)</p>
                      <div className="space-y-1.5">
                        {[
                          completeFile.approval.acknowledgement_1,
                          completeFile.approval.acknowledgement_2,
                          completeFile.approval.acknowledgement_3,
                        ].filter(Boolean).map((ack, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle size={13} className="text-green-600 mt-0.5 shrink-0" />
                            <p className="text-xs text-slate-700">{ack}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Document list */}
                    <div className="border-t border-green-200" />
                    <div>
                      <p className="text-xs font-bold text-green-800 uppercase tracking-wider mb-2">
                        Documents in Approved File ({(completeFile.client_documents?.length || 0) + (completeFile.officer_documents?.length || 0) + (completeFile.government_forms?.length || 0)} total)
                      </p>
                      <div className="space-y-1">
                        {completeFile.client_documents?.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="w-4 h-4 bg-teal-100 text-teal-700 rounded flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                            <span className="text-slate-700">{d.checklist_item_name}{d.document_side_label ? ` — ${d.document_side_label}` : ''}</span>
                            <span className="text-slate-400">(client uploaded)</span>
                          </div>
                        ))}
                        {completeFile.officer_documents?.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="w-4 h-4 bg-indigo-100 text-indigo-700 rounded flex items-center justify-center font-bold shrink-0">{(completeFile.client_documents?.length || 0) + i + 1}</span>
                            <span className="text-slate-700">{d.checklist_item_name}</span>
                            <span className="text-slate-400">(officer prepared)</span>
                          </div>
                        ))}
                        {completeFile.government_forms?.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="w-4 h-4 bg-amber-100 text-amber-700 rounded flex items-center justify-center font-bold shrink-0">{(completeFile.client_documents?.length || 0) + (completeFile.officer_documents?.length || 0) + i + 1}</span>
                            <span className="text-slate-700">{f.name}</span>
                            <span className="text-slate-400">(government form)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Government forms list */}
              {completeFile?.government_forms?.length > 0 && (
                <div className="px-6 py-4">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Government &amp; Filing Forms</p>
                  <div className="space-y-1.5">
                    {completeFile.government_forms.map(f => (
                      <div key={f.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-amber-600 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{f.name}</p>
                            {f.description && <p className="text-xs text-slate-500">{f.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => fileUtils.downloadFile(f.file_url, f.file_name)} className="p-1.5 text-slate-400 hover:text-teal-600"><Download size={14} /></button>
                          {completeFile.status !== 'approved' && (
                            <button onClick={() => handleDeleteGovForm(f.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!completeFile && (
                <div className="text-center py-8 px-6">
                  <Shield size={32} className="mx-auto text-slate-200 mb-2" />
                  <p className="text-sm text-slate-400">Generate the Complete File once all documents are approved</p>
                  <p className="text-xs text-slate-300 mt-0.5">It compiles client docs, officer-prepared docs, and any government forms for client's final approval</p>
                </div>
              )}
            </div>

            {/* Document Review Panel */}
            <div>
              {reviewingDoc ? (
                <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Review Document</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-slate-500">Document</p>
                      <p className="font-medium">{reviewingDoc.checklist_item_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">File</p>
                      <p className="font-medium text-sm">{reviewingDoc.file_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Uploaded</p>
                      <p className="font-medium">{new Date(reviewingDoc.uploaded_at).toLocaleString()}</p>
                    </div>
                    
                    <button
                      onClick={() => setViewingDocument(reviewingDoc)}
                      className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 flex items-center justify-center gap-2"
                    >
                      <Eye size={18} />
                      Open Document
                    </button>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Review Notes
                      </label>
                      <textarea
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Add notes (required for rejection)"
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none resize-none"
                        rows={3}
                      />
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDocumentReview(reviewingDoc.id, 'rejected')}
                        disabled={submitting}
                        className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submitting ? <Loader2 size={18} className="animate-spin" /> : <XCircle size={18} />}
                        Reject
                      </button>
                      <button
                        onClick={() => handleDocumentReview(reviewingDoc.id, 'approved')}
                        disabled={submitting}
                        className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                        Approve
                      </button>
                    </div>

                    <button
                      onClick={() => { setReviewingDoc(null); setReviewNotes(''); }}
                      className="w-full px-4 py-2 text-slate-600 hover:text-slate-900"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-6 text-center">
                  <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Select a document to review</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900">Activity Timeline</h3>
              <button
                onClick={() => { fetchCaseEmails(); }}
                className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                <Mail size={16} />
                View All Emails ({caseEmails.length})
              </button>
            </div>
            <div className="space-y-6">
              {timeline.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No activities recorded yet</p>
              ) : (
                timeline.map((activity, index) => {
                  const isEmailActivity = activity.activity_type?.includes('email');
                  return (
                    <div key={activity.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          activity.activity_type?.includes('approved') ? 'bg-green-100 text-green-600' :
                          activity.activity_type?.includes('rejected') ? 'bg-red-100 text-red-600' :
                          activity.activity_type?.includes('upload') ? 'bg-blue-100 text-blue-600' :
                          isEmailActivity ? 'bg-purple-100 text-purple-600' :
                          'bg-teal-100 text-teal-600'
                        }`}>
                          {activity.activity_type?.includes('approved') ? <CheckCircle size={20} /> :
                           activity.activity_type?.includes('rejected') ? <XCircle size={20} /> :
                           activity.activity_type?.includes('upload') ? <Upload size={20} /> :
                           isEmailActivity ? <Mail size={20} /> :
                           <Clock size={20} />}
                        </div>
                        {index < timeline.length - 1 && (
                          <div className="w-0.5 h-full bg-slate-200 mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{activity.description}</p>
                            <p className="text-sm text-slate-500 mt-1">
                              By {activity.user_name} • {new Date(activity.timestamp).toLocaleString()}
                            </p>
                          </div>
                          {isEmailActivity && (
                            <button
                              onClick={() => {
                                // Find matching email from caseEmails
                                const matchingEmail = caseEmails.find(e => 
                                  activity.description.includes(e.subject) ||
                                  Math.abs(new Date(e.sent_at || e.created_at) - new Date(activity.timestamp)) < 60000
                                );
                                if (matchingEmail) {
                                  handleViewEmail(matchingEmail);
                                } else {
                                  fetchCaseEmails();
                                  toast.info('Loading email details...');
                                }
                              }}
                              className="p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                              title="View Email"
                            >
                              <Eye size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Email View Modal */}
        {showEmailViewModal && selectedEmail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[80vh] flex flex-col">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedEmail.subject}</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedEmail.is_incoming ? 'From' : 'To'}: {selectedEmail.is_incoming ? selectedEmail.from_email : selectedEmail.to_email}
                    <span className="mx-2">•</span>
                    {new Date(selectedEmail.sent_at || selectedEmail.created_at).toLocaleString()}
                  </p>
                </div>
                <button onClick={() => setShowEmailViewModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <XCircle size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-6">
                <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                  {selectedEmail.body}
                </div>
              </div>

              <div className="p-5 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setShowEmailViewModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Stage Checklist</h3>
              <p className="text-sm text-slate-500 mb-4">
                Current Stage: <span className="font-medium text-teal-600">{caseData.current_process_stage}</span>
              </p>
              <div className="space-y-2">
                {taskChecklist.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => toggleTask(task.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      task.done 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {task.done ? (
                      <CheckSquare size={20} className="text-green-500" />
                    ) : (
                      <Square size={20} className="text-slate-400" />
                    )}
                    <span className={`text-sm ${task.done ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                      {task.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Reminders & Alerts</h3>
              <div className="space-y-3">
                {timeRemaining?.doc_deadline_days_remaining !== null && timeRemaining?.doc_deadline_days_remaining <= 5 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <Bell className="text-red-500" size={20} />
                    <div>
                      <p className="font-medium text-red-700">Document Deadline Approaching</p>
                      <p className="text-sm text-red-600">
                        Client has {timeRemaining.doc_deadline_days_remaining} days to submit documents
                      </p>
                    </div>
                  </div>
                )}
                
                {getPendingDocuments().length > 0 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                    <FileText className="text-amber-500" size={20} />
                    <div>
                      <p className="font-medium text-amber-700">Documents Pending Review</p>
                      <p className="text-sm text-amber-600">
                        {getPendingDocuments().length} document(s) waiting for your review
                      </p>
                    </div>
                  </div>
                )}

                {getRejectedDocuments().length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <XCircle className="text-red-500" size={20} />
                    <div>
                      <p className="font-medium text-red-700">Documents Rejected</p>
                      <p className="text-sm text-red-600">
                        {getRejectedDocuments().length} document(s) need to be re-uploaded by client
                      </p>
                    </div>
                  </div>
                )}

                {getUrgencyLevel() === 'normal' && getPendingDocuments().length === 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <CheckCircle className="text-green-500" size={20} />
                    <div>
                      <p className="font-medium text-green-700">All Good!</p>
                      <p className="text-sm text-green-600">
                        No urgent actions required for this case
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div data-testid="notes-tab">
            <CaseNotes caseId={caseId} />
          </div>
        )}

        {/* Send Reminder Modal */}
        {showReminderModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-md mx-4 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Send Reminder</h2>
              <p className="text-slate-600 mb-6">
                This will send an email reminder to {client?.full_name} ({client?.email}) about pending documents or actions required for their case.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowReminderModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReminder}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Send Reminder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Sample Document Modal */}
        {showSampleUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg mx-4 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Upload Sample Document</h2>
              <p className="text-sm text-slate-600 mb-4">
                Upload a sample document for the client to use as a reference.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Document Name *</label>
                  <input
                    type="text"
                    value={sampleDocName}
                    onChange={(e) => setSampleDocName(e.target.value)}
                    placeholder="e.g., Sample Bank Statement"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description (Optional)</label>
                  <textarea
                    value={sampleDocDescription}
                    onChange={(e) => setSampleDocDescription(e.target.value)}
                    placeholder="Add any helpful notes about this sample..."
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">File *</label>
                  <input
                    type="file"
                    onChange={(e) => setSampleDocFile(e.target.files[0])}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none"
                  />
                  {sampleDocFile && (
                    <p className="text-sm text-slate-500 mt-1">Selected: {sampleDocFile.name}</p>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowSampleUploadModal(false);
                    setSampleDocName('');
                    setSampleDocDescription('');
                    setSampleDocFile(null);
                  }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadSampleDoc}
                  disabled={uploadingSample || !sampleDocName || !sampleDocFile}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {uploadingSample ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send Email Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[85vh] flex flex-col">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Send Email to Client</h2>
                <button onClick={() => setShowEmailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <XCircle size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">To</label>
                  <input
                    type="email"
                    value={emailForm.to_email}
                    onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none bg-slate-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={emailForm.subject}
                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                  <textarea
                    value={emailForm.body}
                    onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                    rows={10}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none resize-none"
                  />
                </div>

                <div className="p-3 bg-teal-50 rounded-lg text-sm text-teal-700">
                  <strong>Note:</strong> This email will be automatically linked to case {caseData?.ucin || caseId.substring(0, 8)} 
                  and visible in the activity log.
                </div>
              </div>

              <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {sendingEmail ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share from Library Modal */}
        {showLibraryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[80vh] flex flex-col">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Share from Document Library</h2>
                <button onClick={() => setShowLibraryModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <XCircle size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-5">
                {libraryDocs.length === 0 ? (
                  <div className="text-center py-12">
                    <Library size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No documents in the library yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {libraryDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                      >
                        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                          <FileText size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate">{doc.name}</h4>
                          <p className="text-sm text-slate-500">{doc.category_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                            title="Preview"
                          >
                            <Eye size={18} />
                          </a>
                          <button
                            onClick={() => handleShareFromLibrary(doc.id)}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1"
                          >
                            <Share2 size={14} />
                            Share
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-5 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setShowLibraryModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Document Viewer Modal */}
        {viewingDocument && (
          <DocumentViewer
            fileUrl={viewingDocument.file_url}
            fileName={viewingDocument.file_name}
            onClose={() => setViewingDocument(null)}
          />
        )}

        {/* ── Escalation Modal ── */}
        {showEscalation && (
          <EscalationModal
            onClose={() => setShowEscalation(false)}
            onSuccess={() => setShowEscalation(false)}
            caseId={caseId}
            clientId={caseData?.client_id}
            clientName={client?.full_name}
            ucin={caseData?.ucin}
            visaType={caseData?.visa_program_id}
            caseOfficer={caseData?.assigned_officer_id}
            documents={checklist.map(i => i.name)}
            completeFileAvailable={completeFile?.status === 'approved'}
          />
        )}

        {/* ── Government Form Upload Modal ── */}
        {showGovFormModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowGovFormModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-bold text-slate-900">Upload Government / Filing Form</h3>
                <button onClick={() => setShowGovFormModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X size={18} /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Form Name *</label>
                  <input type="text" value={govFormName} onChange={e => setGovFormName(e.target.value)}
                    placeholder="e.g. IMM 5257 — Temporary Resident Visa Application"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Description <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input type="text" value={govFormDesc} onChange={e => setGovFormDesc(e.target.value)}
                    placeholder="Brief description of this form"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">File *</label>
                  <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={e => setGovFormFile(e.target.files[0])}
                    className="w-full text-sm text-slate-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-teal-50 file:text-teal-700 file:font-medium hover:file:bg-teal-100" />
                </div>
              </div>
              <div className="flex gap-3 px-6 pb-6">
                <button onClick={() => setShowGovFormModal(false)} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">Cancel</button>
                <button onClick={handleUploadGovForm} disabled={uploadingGovForm || !govFormName.trim() || !govFormFile}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-bold hover:bg-amber-600 disabled:opacity-50">
                  {uploadingGovForm ? 'Uploading…' : 'Upload Form'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Request Additional Document Modal ── */}
        {showReqModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowReqModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-bold text-slate-900">Request Additional Document</h3>
                <button onClick={() => setShowReqModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X size={18} /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Document Name *</label>
                  <input
                    type="text" value={reqForm.name} onChange={e => setReqForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Affidavit of Common Residence, Statutory Declaration"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Instructions <span className="text-slate-400 font-normal">(optional)</span></label>
                  <textarea
                    rows={2} value={reqForm.description} onChange={e => setReqForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Describe what format or content is required"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Required From</label>
                    <select
                      value={reqForm.applicant_type}
                      onChange={e => setReqForm(p => ({ ...p, applicant_type: e.target.value, applicant_id: '', applicant_name: '' }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                    >
                      <option value="primary">Primary Applicant</option>
                      {caseData?.family_members?.filter(m => m.type === 'spouse').map(m => (
                        <option key={m.id} value="spouse" onClick={() => setReqForm(p => ({ ...p, applicant_id: m.id, applicant_name: m.full_name }))}>Spouse — {m.full_name}</option>
                      ))}
                      {caseData?.family_members?.filter(m => m.type === 'child').map(m => (
                        <option key={m.id} value="child" onClick={() => setReqForm(p => ({ ...p, applicant_id: m.id, applicant_name: m.full_name }))}>Child — {m.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Required By Date</label>
                    <input
                      type="date" value={reqForm.required_by} onChange={e => setReqForm(p => ({ ...p, required_by: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                  </div>
                </div>
                {/* family member name input shown when a member type is selected but no family members exist */}
                {reqForm.applicant_type !== 'primary' && !caseData?.family_members?.length && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Family Member Name</label>
                    <input
                      type="text" value={reqForm.applicant_name} onChange={e => setReqForm(p => ({ ...p, applicant_name: e.target.value }))}
                      placeholder="Enter name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-3 px-6 pb-6">
                <button onClick={() => setShowReqModal(false)} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all">Cancel</button>
                <button
                  onClick={handleAddRequest} disabled={savingReq || !reqForm.name.trim()}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 disabled:opacity-50 transition-all"
                >
                  {savingReq ? 'Sending…' : 'Send Request to Client'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Visa Program Modal */}
        {showChangeVisaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowChangeVisaModal(false)} data-testid="change-visa-modal">
            <div 
              className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-testid="change-visa-modal-title">
                  <RefreshCw size={20} className="text-amber-600" />
                  Change Visa Program
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  This will update the service validity and checklist for this case.
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Current Program
                  </label>
                  <div className="px-4 py-3 bg-slate-100 rounded-lg text-slate-700" data-testid="current-visa-program">
                    {visaPrograms.find(p => p.id === caseData?.visa_program_id)?.name || 'Unknown'} 
                    <span className="text-slate-500 ml-2">
                      ({visaPrograms.find(p => p.id === caseData?.visa_program_id)?.country})
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    New Visa Program
                  </label>
                  <select
                    value={selectedVisaProgram}
                    onChange={(e) => setSelectedVisaProgram(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    data-testid="select-new-visa-program"
                  >
                    <option value="">Select a program...</option>
                    {visaPrograms.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.country}) - {program.processing_time_days} days
                      </option>
                    ))}
                  </select>
                </div>

                {selectedVisaProgram && selectedVisaProgram !== caseData?.visa_program_id && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Changing the visa program will:
                    </p>
                    <ul className="text-sm text-amber-700 mt-2 ml-4 list-disc">
                      <li>Update service validity to {visaPrograms.find(p => p.id === selectedVisaProgram)?.processing_time_days} days</li>
                      <li>Update client's country and visa type</li>
                      <li>Log this change in the case timeline</li>
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                <button
                  onClick={() => setShowChangeVisaModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                  disabled={changingVisaProgram}
                  data-testid="cancel-change-visa-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangeVisaProgram}
                  disabled={changingVisaProgram || !selectedVisaProgram || selectedVisaProgram === caseData?.visa_program_id}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="submit-change-visa-btn"
                >
                  {changingVisaProgram ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Change Program
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
